"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { searchCommunityShops } from "@/lib/community/api-client";
import { getNextCommunityOffset } from "@/lib/community/pagination";
import type { PublicCommunityShopsSearchQuery } from "@/lib/community/types";

type SearchResponse = Awaited<ReturnType<typeof searchCommunityShops>>;
type Page = SearchResponse & { pageParam: number };

interface UseCommunitySearchOptions {
  enabled?: boolean;
  initialData?: { pages: Page[]; pageParams: number[] };
}

export function useCommunitySearch(
  query: PublicCommunityShopsSearchQuery,
  options: UseCommunitySearchOptions = {},
) {
  return useInfiniteQuery({
    queryKey: ["community-shops-search", query],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await searchCommunityShops({
        ...query,
        skip: pageParam,
        take: query.take ?? 10,
      });
      return { ...response, pageParam } satisfies Page;
    },
    getNextPageParam: (lastPage) =>
      getNextCommunityOffset(lastPage.pageParam, lastPage.shops.length, lastPage.total),
    initialPageParam: 0,
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    initialData: options.initialData,
  });
}
